import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import { axiosInstance } from '../api/axiosConfig';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Projects = () => {
    const { i18n, t } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [backgroundImage, setBackgroundImage] = useState('');

    // Enforce Arabic language
    useEffect(() => {
        i18n.changeLanguage('ar');
    }, [i18n]);

    const textAlignmentClass = 'text-right';
    const flexDirectionClass = 'flex-row-reverse';

    const colors = {
        yellow: '#FFDD33',
        darkBlue: '#003366',
        turquoise: '#218A7A',
    };

    useEffect(() => {
        const fetchProjectsAndBackground = async () => {
            setLoading(true);
            try {
                // Fetch projects
                const projectsResponse = await axiosInstance.get(`/api/projects?lang=${i18n.language}`);
                console.log('Fetched projects:', projectsResponse.data);
                // Filter to ensure projects have an ID for the key and link
                setProjects(projectsResponse.data.filter(project => project.id)); 

                // Fetch background image
                try {
                    const backgroundResponse = await axiosInstance.get('/api/projects/background');
                    setBackgroundImage(backgroundResponse.data.imagePath || '');
                } catch (bgErr) {
                    console.error('Error fetching background:', bgErr);
                    setBackgroundImage('');
                }
                setError('');
            } catch (err) {
                console.error("Error fetching data for projects section:", err);
                // Use a translation key for the error message
                setError(t('error_loading_projects')); 
            } finally {
                setLoading(false);
            }
        };
        fetchProjectsAndBackground();
    }, [i18n.language, t]); // Dependency array includes i18n.language and t

    return (
        <section
            id="projects"
            className="py-16 md:py-24 overflow-hidden relative bg-cover bg-center"
            style={{ backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none' }}
        >
            {/* Background Overlay */}
            {backgroundImage && (
                <div className="absolute inset-0 bg-white opacity-90 dark:bg-gray-900 dark:opacity-80 z-0"></div>
            )}
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
                
                {/* Section Title */}
                <div className={`mb-12 md:mb-16 ${flexDirectionClass} items-center justify-between`}>
                    <h2
                        className={`text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white tracking-tight leading-tight ${textAlignmentClass}`}
                        style={{ color: colors.darkBlue }}
                    >
                        {t('projects_main_title')}
                    </h2>
                </div>
                
                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center gap-3 text-lg py-8" style={{ color: colors.darkBlue }}>
                        <svg className="animate-spin w-7 h-7" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <p>{t('loading_projects')}</p>
                    </div>
                )}
                
                {/* Error State */}
                {error && (
                    <p className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900 p-4 rounded-lg text-center shadow-md animate-pulse">
                        {error}
                    </p>
                )}
                
                {/* No Projects State */}
                {!loading && !error && projects.length === 0 && (
                    <p className={`text-gray-600 dark:text-gray-400 text-center text-lg py-8 ${textAlignmentClass}`}>
                        {t('no_projects_available')}
                    </p>
                )}
                
                {/* Projects Slider */}
                {!loading && !error && projects.length > 0 && (
                    <Swiper
                        modules={[Navigation, Pagination, A11y, Autoplay]} 
                        spaceBetween={30}
                        slidesPerView={1}
                        navigation={true}
                        pagination={{ clickable: true }} 
                        autoplay={{ delay: 8000, disableOnInteraction: false }}
                        loop={true}
                        // Breakpoints for 1, 2, and 3 cards
                        breakpoints={{
                            640: { slidesPerView: 1, spaceBetween: 20 }, 
                            768: { slidesPerView: 2, spaceBetween: 30 }, 
                            1024: { slidesPerView: 3, spaceBetween: 30 }, 
                        }}
                        className="mySwiper !pb-10 h-auto" // h-auto added here. Swiper automatically adjusts slides to match the height of the tallest slide
                    >
                        {projects.map((project, index) => (
                            <SwiperSlide key={project.id} className="h-auto">
                                <div
                                    // *** Key Change: Using h-full (height: 100%) to force card to match SwiperSlide height ***
                                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg transition-all duration-300 transform overflow-hidden border border-gray-100 dark:border-gray-700 group relative flex flex-col items-stretch h-full hover:shadow-xl hover:scale-105"
                                    style={{
                                        borderRightColor: 'transparent',
                                        transition: 'border-right-color 0.3s ease-in-out',
                                        borderLeftColor: 'transparent', // Retained for cross-browser/direction consistency
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderRightColor = colors.turquoise; }} 
                                    onMouseLeave={(e) => { e.currentTarget.style.borderRightColor = 'transparent'; }}
                                >
                                    {/* Project Image and Hover Link (Fixed Height: h-56) */}
                                    {project.image && (
                                        <div className="relative h-56 overflow-hidden flex-shrink-0">
                                            <img
                                                src={project.image}
                                                alt={project.title}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                            />
                                            <Link
                                                to={`/projects/${project.id}`}
                                                className={`absolute inset-0 flex items-center justify-center transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out text-white text-lg font-semibold`}
                                                style={{ backgroundColor: `${colors.darkBlue}E6` }}
                                            >
                                                {t('view_more')}
                                                <svg className="mr-2 h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </Link>
                                        </div>
                                    )}
                                    
                                    {/* Project Content */}
                                    {/* *** Key Change: Removed flex-grow from the content wrap *** */}
                                    <div className="p-6 flex flex-col">
                                        
                                        {/* Project Index (Fixed Content) */}
                                        <span className={`text-3xl font-bold mb-2 ${textAlignmentClass}`} style={{ color: colors.darkBlue }}>
                                            {`0${index + 1}.`}
                                        </span>
                                        
                                        {/* Extra Title (Fixed Content) */}
                                        {project.extra_title && (
                                            <h4 className={`text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1 ${textAlignmentClass}`}>
                                                {project.extra_title}
                                            </h4>
                                        )}
                                        
                                        {/* Main Title (Fixed Content) */}
                                        <h3 className={`font-bold text-xl md:text-2xl text-gray-900 dark:text-white mb-2 ${textAlignmentClass}`}>
                                            {project.title}
                                        </h3>
                                        
                                        {/* Title Description (Fixed Content) */}
                                        {project.title_description && (
                                            // *** Key Change: Removed flex-grow here to stabilize height ***
                                            <p className={`text-gray-600 dark:text-gray-400 mb-4 leading-relaxed ${textAlignmentClass}`}>
                                                {project.title_description}
                                            </p>
                                        )}
                                        
                                        {/* The main project.description remains removed as previously requested */}
                                        
                                    </div>
                                    {/* Added a spacer div to push content up if needed and fill remaining space for true equal height */}
                                    <div className="flex-grow min-h-2"></div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </div>
        </section>
    );
};

export default Projects;